import { DefaultCommission } from "../../ams/DefaultCommission";
import { PaymentIntentMetadata } from "../../stripe";
import { DateTime, NumberFormatter } from "../../util";
import { AttributeOption } from "../AttributeOption";
import { CommissionOption } from "../CommissionOption";
import { PaymentPlan } from "../PaymentPlan";
import { Policy } from "../Policy";
import { PolicyEnums } from "../Policy/Enums";
import { BillingPeriod } from "./BillingPeriod";
import { findCommissionRate } from "./findCommissionRate";

export { findCommissionRate };

interface ISortedBillingPeriods {
    past: BillingPeriod[];
    next: BillingPeriod;
    future: BillingPeriod[];
}

interface ISpecialPayment {
    amount: number;
    date: DateTime;
    formattedDate: string;
    isEscrowDeposit: boolean;
    isDownPayment: boolean;
    invoiceId: string[];
    isPaid: boolean;
}

interface ICommissionByLine {
    commissionOption: CommissionOption;
    totalCommissionablePremium: number;
    totalCommission: number;
    percentageOfPolicyPremium: number;
}
type CommissionByLineMap = Map<string, ICommissionByLine>;

const dateSorter = DateTime.Sorter("DESC");

export abstract class PolicyBilling {
    static #getCtor(
        policy: Policy
    ):
        | typeof CompositePolicyBilling
        | typeof FullPayPolicyBilling
        | typeof PremiumFinancedPolicyBilling
        | typeof InstallmentWithDownPaymentPolicyBilling
        | typeof InstallmentPolicyBilling {
        if (Policy.isComposite(policy)) {
            return CompositePolicyBilling;
        }
        if (policy.paymentPlanId === PaymentPlan.Id.FULL) {
            return FullPayPolicyBilling;
        }
        if (policy.paymentPlanId === PaymentPlan.Id.PREMIUM_FINANCE) {
            return PremiumFinancedPolicyBilling;
        }
        if (PaymentPlan.includesDownPayment(policy.paymentPlanId)) {
            return InstallmentWithDownPaymentPolicyBilling;
        }
        return InstallmentPolicyBilling;
    }

    static reGenerateFromJson(policy: Policy, commissionRates: DefaultCommission[] = []): PolicyBilling {
        if (policy.billing instanceof PolicyBilling) {
            return policy.billing;
        }

        const Ctor = PolicyBilling.#getCtor(policy);

        if (policy.billingJson) {
            const acctDetails = new Ctor(policy, { finalize: false, commissionRates });
            acctDetails.billingPeriods = [];
            policy.billingJson.billPeriods.forEach((pp) => {
                acctDetails.billingPeriods.push(BillingPeriod.from(pp, acctDetails));
            });

            acctDetails.sortedBillingPeriods = {
                future: policy.billingJson.sortedBillPeriods.future.map((pp) => BillingPeriod.from(pp, acctDetails)),
                next:
                    (policy.billingJson.sortedBillPeriods.next &&
                        BillingPeriod.from(policy.billingJson.sortedBillPeriods.next, acctDetails)) ||
                    null,
                past: policy.billingJson.sortedBillPeriods.past.map((pp) => BillingPeriod.from(pp, acctDetails)),
            };

            acctDetails.isPaidOff = policy.billingJson.isPaidOff;
            acctDetails.specialPayment = policy.billingJson.specialPayment;
            acctDetails.totalDue = policy.billingJson.totalDue;
            acctDetails.totalOverdue = policy.billingJson.totalOverdue;
            acctDetails.totalPaid = policy.billingJson.totalPaid;
            acctDetails.totalCharged = policy.billingJson.totalCharged;
            acctDetails.totalUpcoming = policy.billingJson.totalUpcoming;
            acctDetails.totalPaidAndDue = policy.billingJson.totalPaidAndDue;
            acctDetails.totalDueTodayAndOverdue = policy.billingJson.totalDueTodayAndOverdue;
            acctDetails.totalDueNextTodayAndOverdue = policy.billingJson.totalDueNextTodayAndOverdue;
            acctDetails.totalPastOverdue = policy.billingJson.totalPastOverdue;
            acctDetails.totalProjectedCost = policy.billingJson.totalProjectedCost;
            acctDetails.totalProjectedCommission = policy.billingJson.totalProjectedCommission;
            acctDetails.projectedCostPerDay = policy.billingJson.projectedCostPerDay;
            acctDetails.daysUntilAgencyExposure = policy.billingJson.daysUntilAgencyExposure;
            acctDetails.exposureAmount = policy.billingJson.exposureAmount;
            acctDetails.remainingRunway = policy.billingJson.remainingRunway;
            acctDetails.totalDaysInPayPlan = policy.billingJson.totalDaysInPayPlan;
            acctDetails.totalDaysInPolicy = policy.billingJson.totalDaysInPolicy;
            acctDetails.totalDaysIntoPolicy = policy.billingJson.totalDaysIntoPolicy;
            acctDetails.totalDaysIntoPolicyPaidWithoutSpecialPayment = policy.billingJson.totalDaysIntoPolicyPaidWithoutSpecialPayment;
            acctDetails.specialPaymentRunway = policy.billingJson.specialPaymentRunway;
            acctDetails.totalDaysPaidFor = policy.billingJson.totalDaysPaidFor;
            acctDetails.totalPremiumCollateralAmount = policy.billingJson.totalPremiumCollateralAmount;
            acctDetails.agencyExposureDate = new DateTime(policy.billingJson.agencyExposureDate);
            acctDetails.datePaidThrough = new DateTime(policy.billingJson.datePaidThrough);
            acctDetails.isAheadOfPaymentSchedule = policy.billingJson.isAheadOfPaymentSchedule;
            acctDetails.commissionByLine = new Map();
            Object.entries(policy.billingJson.commissionByLine).forEach(([key, value]) => {
                acctDetails.commissionByLine.set(key, value);
            });
            policy.billing = acctDetails;
            return acctDetails;
        }

        return null;
    }

    static isFullBillingPeriod({ billingPeriod, isComposite }: { billingPeriod: BillingPeriod; isComposite: boolean }): boolean {
        if (isComposite) {
            const start = DateTime.first(billingPeriod.startDate);
            const end = DateTime.last(billingPeriod.startDate);
            return start.isEqual(billingPeriod.startDate) && end.isEqual(billingPeriod.endDate);
        }

        return billingPeriod.endDate.isBeforeOrEqual(DateTime.addMonths(billingPeriod.startDate, 1));
    }

    static getPreviousBillingPeriodWithPayment(billPeriods: BillingPeriod[], baseBillingPeriod: BillingPeriod): BillingPeriod {
        const index = billPeriods.indexOf(baseBillingPeriod);
        if (index === -1 || index === 0) return baseBillingPeriod;
        const target = billPeriods.at(index - 1);
        if (target.hasPaidSome) return target;
        return PolicyBilling.getPreviousBillingPeriodWithPayment(billPeriods, target);
    }

    static new(
        { policy, commissionRates, today }: { policy: Policy; commissionRates: DefaultCommission[]; today?: DateTime; },
        options?: { finalize: boolean }
    ): PolicyBilling {
        const Ctor = PolicyBilling.#getCtor(policy);
        return new Ctor(policy, { commissionRates, finalize: options?.finalize, today });
    }

    static getBillingPeriods({ policy }: { policy: Policy }): BillingPeriod[] {
        const details = PolicyBilling.new(
            {
                policy,
                commissionRates: [],
            },
            { finalize: false }
        );
        return details.billingPeriods;
    }

    static #calculateTotalPaid({ billPeriods }: { billPeriods: BillingPeriod[] }): number {
        return billPeriods.reduce((tot, bp) => tot + bp.paid, 0);
    }

    static #calculateTotalCharged({ billPeriods }: { billPeriods: BillingPeriod[] }): number {
        return billPeriods.reduce((tot, bp) => tot + bp.charged, 0);
    }

    billingPeriods: BillingPeriod[] = [];
    sortedBillingPeriods: ISortedBillingPeriods = {
        past: [],
        next: null,
        future: [],
    };
    totalPayments = NaN;
    effectiveDate: DateTime;
    expirationDate: DateTime;
    isPaidOff: boolean = null;
    specialPayment: ISpecialPayment = null;
    totalDue: number;
    totalOverdue: number;
    totalDueTodayAndOverdue: number;
    totalDueNextTodayAndOverdue: number;
    totalPastOverdue: number;
    totalPaid: number;
    totalCharged: number;
    totalUpcoming: number;
    totalPaidAndDue: number;
    totalProjectedCost: number;
    totalProjectedCommission: number;
    totalDaysInPayPlan: number;
    totalDaysInPolicy: number;
    totalDaysIntoPolicy: number;
    totalDaysIntoPolicyPaidWithoutSpecialPayment: number;
    specialPaymentRunway: number;
    totalDaysPaidFor: number;
    totalPremiumCollateralAmount: number;
    projectedCostPerDay: number;
    daysUntilAgencyExposure: number;
    exposureAmount: number;
    remainingRunway: number;
    agencyExposureDate: DateTime;
    datePaidThrough: DateTime;

    options: PolicyBilling.IOptions;

    isAheadOfPaymentSchedule: boolean;
    commissionByLine: CommissionByLineMap;

    isCompositeArrears: boolean;
    isCompositeAdvanced: boolean;
    isCompositeDoubleAdvanced: boolean;
    isComposite: boolean;

    protected constructor(readonly policy: Policy, options?: PolicyBilling.IOptions) {
        this.effectiveDate = DateTime.fromString(new Date(policy.effectiveDate).toISOString().slice(0, 10), "YYYY-MM-DD").resetTime();
        this.expirationDate = DateTime.fromString(new Date(policy.expirationDate).toISOString().slice(0, 10), "YYYY-MM-DD").resetTime();
        this.isCompositeArrears = Policy.isCompositeArrears(policy);
        this.isCompositeAdvanced = Policy.isCompositeAdvanced(policy);
        this.isCompositeDoubleAdvanced = Policy.isCompositeDoubleAdvanced(policy);
        this.isComposite = this.isCompositeAdvanced || this.isCompositeArrears || this.isCompositeDoubleAdvanced;
        this.totalPayments = PaymentPlan.getTotalPayments(policy.paymentPlanId);
        this.totalDaysInPayPlan = PaymentPlan.getTotalDaysInPayPlan(policy.paymentPlanId, this.effectiveDate);
        this.totalDaysInPolicy = DateTime.differenceInCalendarDays(policy.expirationDate, this.effectiveDate, { includeEndDate: true });
        this.totalDaysIntoPolicy = DateTime.differenceInCalendarDays(getTodayOrExpirationDate(this.expirationDate), this.effectiveDate, {
            includeEndDate: true,
        });
        this.options = {
            finalize: options?.finalize ?? true,
            commissionRates: options?.commissionRates ?? [],
        };
        if (this.totalDaysIntoPolicy < 0) {
            this.totalDaysIntoPolicy = 0;
        }

        this.buildPayPlanSchedule(options?.today);
        if (this.options.finalize) this.finalize();
    }

    add(billingPeriod: BillingPeriod | Omit<BillingPeriod.Options, "schedule">): void {
        if (billingPeriod instanceof BillingPeriod) {
            this.billingPeriods.push(billingPeriod);
        }
        const Ctor = BillingPeriod.getConstructor(this);
        this.billingPeriods.push(
            new Ctor({
                ...billingPeriod,
                schedule: this,
            })
        );
    }

    get length(): number {
        return this.billingPeriods.length;
    }

    get pastLength(): number {
        return this.billingPeriods.filter((bp) => bp.isPast).length;
    }
    get futureLength(): number {
        return this.billingPeriods.filter((bp) => bp.isFuture).length;
    }

    get isPast(): boolean {
        return this.billingPeriods.every((x) => x.isPast);
    }

    get isFuture(): boolean {
        return this.billingPeriods.every((x) => x.isFuture);
    }

    get current(): BillingPeriod {
        return this.getCurrent();
    }

    get totalFutureBillingPeriods(): number {
        return this.sortedBillingPeriods.future.length;
    }

    get isFirstBillingPeriodCurrent(): boolean {
        const current = this.getCurrent();
        return this.billingPeriods.length === 1 ? true : current?.startDate.valueOf() === this.first().startDate.valueOf();
    }

    getHasSomeOverdue(): boolean {
        return this.billingPeriods.some((bp) => bp.isOverdue);
    }

    first(): BillingPeriod {
        return this.billingPeriods[0];
    }

    last(): BillingPeriod {
        return this.billingPeriods.at(-1);
    }

    getFirstDueDate(): DateTime {
        return this.billingPeriods.at(0).dueDate;
    }

    getFinalDueDate(): DateTime {
        return this.billingPeriods.at(-1).dueDate;
    }

    getCurrentDue(): BillingPeriod {
        if (this.billingPeriods.length === 1) return this.billingPeriods[0];
        const period = this.billingPeriods.find((bp) => {
            if (DateTime.today.isBeforeOrEqual(bp.dueDate)) return bp;
            return null;
        });
        if (period) return period;
        if (this.billingPeriods.at(-1).dueDate.isBefore(DateTime.today)) return this.billingPeriods.at(-1);
        return this.billingPeriods[0];
    }

    getDue(): { amount: number; projected: boolean } {
        const amount = this.billingPeriods.reduce((acc, bp) => {
            const bpDue = bp.getDue();
            if (bpDue.projected) return acc;
            return acc + bpDue.amount;
        }, 0);
        return {
            amount,
            projected: false,
        };
    }

    getDueAndCurrentProjectedDue(): { amount: number; projected: boolean } {
        let hasProjectedAmount = false;
        const amount = this.billingPeriods
            .filter((bp) => bp.isCurrent || bp.isPast)
            .reduce((acc, bp) => {
                const bpDue = bp.getDue();
                if (bpDue.projected) {
                    hasProjectedAmount = true;
                }
                return acc + bpDue.amount;
            }, 0);
        return {
            amount,
            projected: hasProjectedAmount,
        };
    }

    getCurrent(): BillingPeriod {
        return this.billingPeriods.find((x) => x.isCurrent);
    }

    getPreviousBillingPeriodWithPayment(baseBillingPeriod: BillingPeriod): BillingPeriod {
        return PolicyBilling.getPreviousBillingPeriodWithPayment(this.billingPeriods, baseBillingPeriod);
    }

    isCurrentOnPayment(): boolean {
        if (this.policy.totalPaid >= this.policy.totalBilled) return true;

        return !this.billingPeriods.some((bp) => bp.isOverdue);
    }

    hasPaidSome(): boolean {
        return this.billingPeriods.some((bp) => bp.paid > 0);
    }

    isFullBillingPeriod(billingPeriod: BillingPeriod): boolean {
        return PolicyBilling.isFullBillingPeriod({ billingPeriod, isComposite: this.isComposite });
    }

    isFirstBillingPeriod(billingPeriod: BillingPeriod): boolean {
        return billingPeriod.isFirst;
    }

    calculatePaymentConsistencyScore(): number {
        return BillingPeriod.calculatePaymentConsistencyScore(this.billingPeriods);
    }

    getLatestMonthPaidBillingPeriod(): BillingPeriod | null {
        return [...this.billingPeriods].reverse().find((bp) => bp.hasPaidSome) || null;
    }

    getBillingPeriodForDueDate(dueDate: Date): BillingPeriod | null {
        const _dueDate = new DateTime(dueDate);
        return this.billingPeriods.find((bp) => {
            if (_dueDate.isEqual(bp.dueDate, { excludeTime: true })) return bp;
            return null;
        });
    }

    getBillingPeriodForMonthAndYear({ month, year }: { month: number; year: number }): BillingPeriod | null {
        return BillingPeriod.getBillingPeriodForMonthAndYear({
            billingPeriods: this.billingPeriods,
            month,
            year,
        });
    }

    json(): PolicyBilling.JSON {
        const commissionByLine = {};
        this.commissionByLine.forEach((value, key) => {
            commissionByLine[key] = value;
        });

        return {
            billPeriods: this.billingPeriods.map((bp) => bp.json()),
            sortedBillPeriods: {
                future: this.sortedBillingPeriods.future.map((bp) => bp.json()),
                next: this.sortedBillingPeriods.next?.json(),
                past: this.sortedBillingPeriods.past.map((bp) => bp.json()),
            },
            isPaidOff: this.isPaidOff,
            specialPayment: this.specialPayment,
            totalDue: this.totalDue,
            totalOverdue: this.totalOverdue,
            totalPaid: this.totalPaid,
            totalCharged: this.totalCharged,
            totalUpcoming: this.totalUpcoming,
            totalPaidAndDue: this.totalPaidAndDue,
            totalDueTodayAndOverdue: this.totalDueTodayAndOverdue,
            totalDueNextTodayAndOverdue: this.totalDueNextTodayAndOverdue,
            totalPastOverdue: this.totalPastOverdue,
            totalProjectedCost: this.totalProjectedCost,
            totalProjectedCommission: this.totalProjectedCommission,
            projectedCostPerDay: this.projectedCostPerDay,
            daysUntilAgencyExposure: this.daysUntilAgencyExposure,
            exposureAmount: this.exposureAmount,
            remainingRunway: this.remainingRunway,
            totalDaysInPayPlan: this.totalDaysInPayPlan,
            totalDaysInPolicy: this.totalDaysInPolicy,
            totalDaysIntoPolicy: this.totalDaysIntoPolicy,
            totalDaysIntoPolicyPaidWithoutSpecialPayment: this.totalDaysIntoPolicyPaidWithoutSpecialPayment,
            specialPaymentRunway: this.specialPaymentRunway,
            totalDaysPaidFor: this.totalDaysPaidFor,
            totalPremiumCollateralAmount: this.totalPremiumCollateralAmount,
            agencyExposureDate: this.agencyExposureDate,
            datePaidThrough: this.datePaidThrough,
            isAheadOfPaymentSchedule: this.isAheadOfPaymentSchedule,
            commissionByLine,

            totalPayments: this.totalPayments,
            effectiveDate: this.effectiveDate,
            expirationDate: this.expirationDate,
            isCompositeArrears: this.isCompositeArrears,
            isCompositeAdvanced: this.isCompositeAdvanced,
            isComposite: this.isComposite,
        };
    }

    #getPolicyCommissionsByLine(): CommissionByLineMap {
        if (!this.policy.allCommissions?.length) return new Map();
        const totalCommissionablePremium =
            this.policy.allCommissions.reduce((acc, comm) => {
                return acc + comm.premium;
            }, 0) || 0;

        const map = this.policy.allCommissions.reduce((acc, comm) => {
            if (!comm.commissionOptionCode || !this.options.commissionRates.length) return acc;

            if (!acc.has(comm.commissionOptionCode)) {
                const commissionRate = findCommissionRate({
                    commissionRates: this.options.commissionRates,
                    writingCompanyCode: this.policy.writingCompanyCode,
                    parentCompanyCode: this.policy.parentCompanyCode,
                    commissionOptionCode: comm.commissionOptionCode,
                    policyTransactionType: this.policy.policyTransactionType,
                    policyEffectiveDate: this.policy.effectiveDate,
                });
                const metadata = commissionRate?.convertToDto();

                acc.set(comm.commissionOptionCode, {
                    commissionOption: {
                        code: commissionRate?.LineOfBusinessCode,
                        description: commissionRate?.LineOfBusiness?.Description,
                        metadata,
                    },
                    totalCommissionablePremium: comm.premium || 0,
                    percentageOfPolicyPremium: NaN,
                    totalCommission: NaN,
                });
                return acc;
            }
            const existing = acc.get(comm.commissionOptionCode);
            if (existing?.commissionOption?.metadata?.rate) {
                existing.totalCommissionablePremium += comm.premium;
            }
            acc.set(comm.commissionOptionCode, existing);
            return acc;
        }, new Map<string, ICommissionByLine>());

        map.forEach((v) => {
            v.totalCommission = v.totalCommissionablePremium * ((v.commissionOption.metadata?.rate || 0) * 0.01);
            v.percentageOfPolicyPremium = (v.totalCommissionablePremium / totalCommissionablePremium) * 100;
        });

        return map;
    }

    #calculateTotalProjectedCommissions({
        totalProjectedCost,
        commissionByLine,
    }: {
        totalProjectedCost: number;
        commissionByLine: CommissionByLineMap;
    }): number {
        const byLine = Array.from(commissionByLine.values());
        const total = byLine.reduce((acc, val) => {
            return acc + val.percentageOfPolicyPremium * 0.01 * totalProjectedCost * (val.commissionOption?.metadata?.rate * 0.01 || 0);
        }, 0);
        if (isNaN(total)) {
            return 0;
        }
        return total;
    }

    abstract buildPayPlanSchedule(today?: DateTime): void;
    abstract setSpecialPayments(): void;

    protected _setPastCurrentFuture(today = DateTime.today): void {
        if (today.isBeforeOrEqual(this.billingPeriods[0].dueDate)) {
            this.billingPeriods[0].isCurrent = true;
        } else if (today.isAfterOrEqual(this.billingPeriods.at(-1).dueDate)) {
            this.billingPeriods.at(-1).isCurrent = true;
        } else {
            const currentBP = this.billingPeriods.find((bp) => today.isBefore(bp.dueDate));
            if (currentBP) {
                currentBP.isCurrent = true;
            } else {
                this.billingPeriods.at(-1).isCurrent = true;
            }
        }

        let hasSeenCurrent = false;
        this.billingPeriods.forEach((bp) => {
            let isPast = null,
                isCurrent = null,
                isFuture = null;

            if (bp.isCurrent) {
                hasSeenCurrent = true;
                isPast = false;
                isCurrent = true;
                isFuture = false;
            } else if (hasSeenCurrent) {
                isPast = false;
                isCurrent = false;
                isFuture = true;
            } else {
                isPast = true;
                isCurrent = false;
                isFuture = false;
            }

            bp.isPast = isPast;
            bp.isCurrent = isCurrent;
            bp.isFuture = isFuture;
        });
    }

    finalize(): void {
        this.setAccountingData();
        const { policy } = this;

        // if (this.isPast) {
        //     this.billPeriods.at(-1).isCurrent = true;
        // } else if (this.isFuture) {
        //     this.billPeriods.at(0).isCurrent = true;
        // } else if (this.billPeriods.every((bp) => !bp.isCurrent)) {
        //     this.billPeriods.at(0).isCurrent = true;
        // }

        this.setSpecialPayments();

        // if (specialPayment.isEscrowDeposit) {
        //     const billingPeriodWithEscrowDeposit = this.first();
        //     billingPeriodWithEscrowDeposit.charged -= NumberFormatter.round(specialPayment.amount);
        //     const invoiceWithEscrowDeposit = billingPeriodWithEscrowDeposit.invoices.find((i) => Invoice.includesEscrowDeposit({ invoice: i }));
        //     if (invoiceWithEscrowDeposit.isPaid) {
        //         billingPeriodWithEscrowDeposit.paid -= NumberFormatter.round(specialPayment.amount);
        //     }
        // } else if (specialPayment.isDownPayment) {
        //     const billingPeriodWithDownPayment = this.first();
        //     billingPeriodWithDownPayment.charged = NumberFormatter.round(specialPayment.amount);
        //     // const invoiceWithDownPayment = billingPeriodWithDownPayment.invoices.find((i) => Invoice.includesDownPayment({ invoice: i }));
        //     // if (invoiceWithDownPayment.isPaid) {
        //     //     billingPeriodWithDownPayment.paid -= NumberFormatter.round(specialPayment.amount);
        //     // }
        // }

        let hasAssignedNext = false;
        for (const bp of this.billingPeriods) {
            if (bp.isPast) {
                this.sortedBillingPeriods.past.push(bp);
            } else if (hasAssignedNext) {
                this.sortedBillingPeriods.future.push(bp);
            } else {
                this.sortedBillingPeriods.next = bp;
                hasAssignedNext = true;
            }
        }

        const totalPaid = PolicyBilling.#calculateTotalPaid({ billPeriods: this.billingPeriods });
        const totalCharged = PolicyBilling.#calculateTotalCharged({ billPeriods: this.billingPeriods });

        const currentBillingPeriod = this.getCurrent();
        const latestBillingPeriodWithPayment = this.getLatestMonthPaidBillingPeriod();
        this.totalDaysIntoPolicyPaidWithoutSpecialPayment = this.billingPeriods.reduce((acc, bp) => {
            if (
                (bp.isPaid && bp.hasPaidSome) ||
                (policy.billMethod === PolicyEnums.BillMethod.DIRECT && latestBillingPeriodWithPayment?.startDate.isAfter(bp.endDate))
            ) {
                return acc + DateTime.differenceInCalendarDays(bp.endDate, bp.startDate, { includeEndDate: true });
            }
            if (!bp.isPaid && bp.hasPaidSome) {
                const totalDaysInPeriod = DateTime.differenceInCalendarDays(bp.endDate, bp.startDate, { includeEndDate: true });
                const percentPaid = bp.paid / bp.charged;
                const totalDaysPaidFor = totalDaysInPeriod * percentPaid;
                return acc + totalDaysPaidFor;
            }
            return acc;
        }, 0);
        this.isPaidOff = totalPaid >= policy.fullTermPremium && this.billingPeriods.every((bp) => !bp.isFuture && bp.isPaid);

        this.projectedCostPerDay = this.calculateTotalProjectedCostPerDay({
            policy,
            specialPayment: this.specialPayment,
            totalPayments: this.totalPayments,
            totalPaid,
            totalCharged,
            billPeriods: this.billingPeriods,
            nextBillPeriod: this.sortedBillingPeriods.next,
            totalPaidAndDue: this.totalPaidAndDue,
            effectiveDate: this.effectiveDate,
            expirationDate: this.expirationDate,
            fullTermPremiumAtBind: policy.fullTermPremium,
            isCompositeAdvanced: this.isCompositeAdvanced,
            isCompositeArrears: this.isCompositeArrears,
            totalDaysIntoPolicyPaidWithoutSpecialPayment: this.totalDaysIntoPolicyPaidWithoutSpecialPayment,
            totalDaysInPolicy: this.totalDaysInPolicy,
            isPaidOff: this.isPaidOff,
        });
        const totalDue = this.sortedBillingPeriods.next?.getDue().amount || 0;
        this.totalUpcoming = this.sortedBillingPeriods.future.reduce((acc, bp) => {
            return acc + bp.getDue().amount;
        }, 0);
        this.totalOverdue = this.calculateTotalOverdue({ billPeriods: this.billingPeriods });

        this.totalPaidAndDue = totalPaid + totalDue;
        this.totalDue = totalDue;
        this.totalPaid = totalPaid;
        this.totalCharged = totalCharged;
        this.isAheadOfPaymentSchedule = totalPaid > this.totalCharged;
        this.totalPastOverdue = this.calculateTotalPastOverdue({
            sortedBillPeriods: this.sortedBillingPeriods,
        });
        // this.totalProjectedCost = totalPaid + totalDue + this.totalUpcoming + this.totalPastOverdue;
        this.totalProjectedCost = this.billingPeriods.reduce((acc, bp) => {
            return acc + bp.charged;
        }, 0);

        this.totalDueTodayAndOverdue = this.totalPastOverdue;
        if (currentBillingPeriod?.totalDue > 0 && !currentBillingPeriod?.isOverdue) {
            this.totalDueTodayAndOverdue += currentBillingPeriod.totalDue;
        }
        this.totalDueNextTodayAndOverdue = this.totalPastOverdue + totalDue;

        this.totalPremiumCollateralAmount = Policy.getAttribute(policy, AttributeOption.Id.PREMIUM_COLLATERAL)?.valueNumber || 0;
        this.commissionByLine = this.#getPolicyCommissionsByLine();
        this.totalProjectedCommission = this.#calculateTotalProjectedCommissions({
            commissionByLine: this.commissionByLine,
            totalProjectedCost: this.totalProjectedCost,
        });

        this.specialPaymentRunway = this.calculateSpecialPaymentRunway({
            projectedCostPerDay: this.projectedCostPerDay,
        });
        this.totalDaysPaidFor = this.calculateTotalDaysPaidFor({
            specialPaymentRunway: this.specialPaymentRunway,
            billPeriods: this.billingPeriods,
            projectedCostPerDay: this.projectedCostPerDay,
        });
        this.datePaidThrough = this.calculateDatePaidThrough({
            effectiveDate: this.effectiveDate,
            totalDaysPaidFor: this.totalDaysPaidFor,
        });
        this.agencyExposureDate = this.calculateAgencyExposureDate({
            datePaidThrough: this.datePaidThrough,
        });
        this.daysUntilAgencyExposure = this.calculateDaysUntilAgencyExposure({
            agencyExposureDate: this.agencyExposureDate,
            expirationDate: this.expirationDate,
        });
        this.exposureAmount = this.calculateExposureAmount({
            totalProjectedCost: this.totalProjectedCost,
            daysUntilAgencyExposure: this.daysUntilAgencyExposure,
            projectedCostPerDay: this.projectedCostPerDay,
            isPaidOff: this.isPaidOff,
        });
        this.remainingRunway = this.calculateRemainingRunway({
            specialPaymentRunway: this.specialPaymentRunway,
            totalDueTodayAndOverdue: this.totalDueTodayAndOverdue,
            totalPremiumCollateralAmount: this.totalPremiumCollateralAmount,
            specialPaymentAmount: this.specialPayment.amount,
        });
    }

    calculateTotalOverdue({ billPeriods }: { billPeriods: BillingPeriod[] }): number {
        return billPeriods.reduce((tot, bp) => {
            if (bp.isOverdue) {
                return tot + bp.charged - bp.paid;
            }
            return tot;
        }, 0);
    }

    calculateRemainingRunway({
        specialPaymentRunway,
        totalPremiumCollateralAmount,
        totalDueTodayAndOverdue,
        specialPaymentAmount,
    }: {
        specialPaymentRunway: number;
        totalPremiumCollateralAmount: number;
        totalDueTodayAndOverdue: number;
        specialPaymentAmount: number;
    }): number {
        if (specialPaymentRunway === 0 && totalPremiumCollateralAmount === 0) return 0;
        return totalPremiumCollateralAmount + specialPaymentAmount - totalDueTodayAndOverdue;
    }

    calculateExposureAmount({
        projectedCostPerDay,
        totalProjectedCost,
        daysUntilAgencyExposure,
        isPaidOff,
    }: {
        projectedCostPerDay: number;
        totalProjectedCost: number;
        daysUntilAgencyExposure: number;
        isPaidOff: boolean;
    }): number {
        if (isPaidOff) return 0;
        const amount = projectedCostPerDay * daysUntilAgencyExposure;
        if (Math.abs(amount) > totalProjectedCost) {
            if (amount < 0) return -totalProjectedCost;
            return totalProjectedCost;
        }
        return amount;
    }

    calculateDaysUntilAgencyExposure({
        agencyExposureDate,
        expirationDate,
    }: {
        agencyExposureDate: DateTime;
        expirationDate: DateTime;
    }): number {
        return DateTime.differenceInCalendarDays(agencyExposureDate, getTodayOrExpirationDate(expirationDate));
    }

    calculateDatePaidThrough({ effectiveDate, totalDaysPaidFor }: { effectiveDate: DateTime; totalDaysPaidFor: number }): DateTime {
        return DateTime.addDays(effectiveDate, totalDaysPaidFor);
    }

    calculateAgencyExposureDate({ datePaidThrough }: { datePaidThrough: DateTime }): DateTime {
        let exposureDate = datePaidThrough;
        if (this.policy.description && this.policy.description.includes("AL") && !this.policy.description.includes("XSAL")) {
            exposureDate = DateTime.subtractDays(datePaidThrough, 35);
        }
        if (exposureDate.isBefore(this.policy.effectiveDate)) {
            return new DateTime(this.policy.effectiveDate);
        }
        return exposureDate;
    }

    calculateTotalDaysPaidFor({
        specialPaymentRunway,
        billPeriods,
        projectedCostPerDay,
    }: {
        projectedCostPerDay: number;
        specialPaymentRunway: number;
        billPeriods: BillingPeriod[];
    }): number {
        let daysPaidForStart = 0;

        if (specialPaymentRunway > 0) {
            daysPaidForStart += specialPaymentRunway;
        }

        const total = billPeriods.reduce((acc, bp) => {
            if (bp.isPaid && bp.charged > 0) {
                return acc + DateTime.differenceInCalendarDays(bp.endDate, bp.startDate, { includeEndDate: true });
            }
            if (bp.hasPaidSome && !bp.isPaid) {
                if (bp.paid < projectedCostPerDay) {
                    return acc;
                }
                return acc + bp.paid / projectedCostPerDay;
            }
            return acc;
        }, daysPaidForStart);
        return Math.floor(total);
    }

    calculateSpecialPaymentRunway({ projectedCostPerDay }: { projectedCostPerDay: number }): number {
        const premiumCollateralAttr = Policy.getAttribute(this.policy, AttributeOption.Id.PREMIUM_COLLATERAL);
        if (this.specialPayment.amount === 0 && !premiumCollateralAttr) return 0;
        return Math.floor((this.specialPayment.amount || premiumCollateralAttr?.valueNumber || 0) / projectedCostPerDay);
    }

    setAccountingData(): void {
        const isDirectBill = this.policy.billMethod === Policy.BillMethod.DIRECT;

        this.policy.invoices
            ?.filter((i) => i.number > 0)
            .forEach((inv) => {
                const useDueDateForBillingPeriodSlotting = this.isComposite && Boolean(inv.dueDate);
                const targetDate = isDirectBill
                    ? inv.enteredDate
                    : // : inv.effectiveDate;
                    inv.dueDate && !isNaN(inv.dueDate.valueOf())
                    ? inv.dueDate
                    : inv.effectiveDate;
                const dueDate = DateTime.fromString(targetDate.toISOString().slice(0, 10), "YYYY-MM-DD");

                let targetBillingPeriod: BillingPeriod;

                if (this.billingPeriods.length === 1) {
                    targetBillingPeriod = this.billingPeriods[0];
                } else {
                    targetBillingPeriod = this.billingPeriods.find((bp, i, arr) => {
                        if (dueDate.isEqual(bp.dueDate, { excludeTime: true })) return bp;

                        const isBetween = dueDate.isBetween(bp.startDate, useDueDateForBillingPeriodSlotting ? bp.dueDate : bp.endDate, {
                            excludeTime: true,
                            equalStart: true,
                            equalEnd: true,
                        });
                        if (isBetween) return bp;
                        if (i === 0 && dueDate.isBeforeOrEqual(bp.startDate)) return bp;
                        if (i === arr.length - 1 && dueDate.isAfterOrEqual(bp.endDate)) return bp;
                        return null;
                    });

                    if (!targetBillingPeriod && useDueDateForBillingPeriodSlotting) {
                        targetBillingPeriod = this.billingPeriods.find((sc, i, arr) => {
                            const isBetween = dueDate.isBetween(sc.startDate, sc.endDate, {
                                excludeTime: true,
                                equalStart: true,
                                equalEnd: true,
                            });
                            if (isBetween) return sc;
                            if (i === 0 && dueDate.isBeforeOrEqual(sc.startDate)) return sc;
                            if (i === arr.length - 1 && dueDate.isAfterOrEqual(sc.endDate)) return sc;
                            return null;
                        });
                    }

                    if (!targetBillingPeriod) {
                        console.warn({
                            message: "Unable to find pay period for invoice",
                            useDueDateForBillingPeriodSlotting,
                            policy: {
                                number: this.policy.number,
                                isComposite: this.isComposite,
                            },
                            invoice: {
                                dueDate: inv.dueDate,
                                effectiveDate: inv.effectiveDate,
                                enteredDate: inv.enteredDate,
                            },
                            dueDate: dueDate,
                            billPeriods: this.billingPeriods.map((bp) => ({
                                start: bp.startDate,
                                end: bp.endDate,
                                premiumDue: bp.dueDate,
                            })),
                        });
                        targetBillingPeriod = this.billingPeriods[0];
                    }
                }

                targetBillingPeriod.invoices.push(inv);
            });

        this.policy.charges?.forEach((c) => {
            const billingPeriodIdx = parseInt(c.metadata[PaymentIntentMetadata.billingPeriodIndex] || "0");
            const targetBillingPeriod = this.billingPeriods.find((bp) => bp.index === billingPeriodIdx);
            targetBillingPeriod.addCharge(c);
        });

        this.billingPeriods.forEach((bp) => {
            bp.invoices = bp.invoices.sort((a, b) => a.number - b.number);
        });
    }

    calculateTotalPastOverdue({ sortedBillPeriods }: { sortedBillPeriods: ISortedBillingPeriods }): number {
        const dueFromPastPeriods = sortedBillPeriods.past.reduce((tot, bp) => tot + bp.getDue().amount, 0);
        if (!sortedBillPeriods.next) return dueFromPastPeriods;
        if (DateTime.today.isBeforeOrEqual(sortedBillPeriods.next.dueDate)) return dueFromPastPeriods;
        const dueFromCurrentBillPeriod = sortedBillPeriods.next.charged - sortedBillPeriods.next.paid;
        return dueFromPastPeriods + dueFromCurrentBillPeriod;
    }

    calculateTotalProjectedCostPerDay({
        totalPayments,
        totalPaid,
        totalCharged,
        billPeriods,
        nextBillPeriod,
        isCompositeArrears,
        isCompositeAdvanced,
        totalDaysIntoPolicyPaidWithoutSpecialPayment,
        totalDaysInPolicy,
        effectiveDate,
        expirationDate,
        fullTermPremiumAtBind,
        isPaidOff,
        policy,
        specialPayment,
    }: {
        totalPayments: number;
        totalPaid: number;
        totalCharged: number;
        billPeriods: BillingPeriod[];
        nextBillPeriod: BillingPeriod;
        totalPaidAndDue: number;
        isCompositeArrears: boolean;
        isCompositeAdvanced: boolean;
        totalDaysIntoPolicyPaidWithoutSpecialPayment: number;
        totalDaysInPolicy: number;
        effectiveDate: DateTime;
        expirationDate: DateTime;
        fullTermPremiumAtBind: number;
        isPaidOff: boolean;
        policy: Policy;
        specialPayment: ISpecialPayment;
    }): number {
        if (isPaidOff) {
            return totalPaid / totalDaysInPolicy;
        }
        const firstBillingPeriod = billPeriods[0];
        if (
            totalPayments === 1 &&
            totalPaid > 0 &&
            !firstBillingPeriod.isPaid &&
            nextBillPeriod &&
            nextBillPeriod.charged <= nextBillPeriod.paid
        ) {
            return NumberFormatter.round(firstBillingPeriod.charged);
        }

        const today = getTodayOrExpirationDate(expirationDate);
        if (
            totalPaid <= 0 ||
            today.isBefore(effectiveDate) ||
            (isCompositeArrears && today.isBefore(DateTime.addDays(effectiveDate, 40))) ||
            (isCompositeAdvanced && today.isBefore(DateTime.addDays(effectiveDate, 10)))
        ) {
            return fullTermPremiumAtBind / totalDaysInPolicy;
        }

        if (billPeriods.every((bp) => bp.hasCharge)) {
            return totalCharged / totalDaysInPolicy;
        }

        if (PaymentPlan.includesDownPayment(policy.paymentPlanId) && specialPayment.isDownPayment) {
            return policy.totalCost / totalDaysInPolicy;
        }

        // const currentBillingPeriod = billPeriods.find((bp) => bp.isCurrent);
        const isAbovePaymentConsistencyThreshold = BillingPeriod.isAbovePaymentConsistencyThreshold(billPeriods);

        if (isAbovePaymentConsistencyThreshold) {
            return (
                billPeriods.reduce((acc, bp) => {
                    return acc + bp.getCharged().amount;
                }, 0) / totalDaysInPolicy
            );
        }

        return totalPaid / totalDaysIntoPolicyPaidWithoutSpecialPayment;
    }
}

export namespace PolicyBilling {
    export interface IOptions {
        finalize: boolean;
        commissionRates: DefaultCommission[];
        today?: DateTime;
    }
    export interface JSON {
        billPeriods: BillingPeriod[];
        sortedBillPeriods: {
            past: BillingPeriod[];
            next: BillingPeriod;
            future: BillingPeriod[];
        };
        isPaidOff: boolean;
        specialPayment: ISpecialPayment;
        totalDue: number;
        totalOverdue: number;
        totalPaid: number;
        totalCharged: number;
        totalUpcoming: number;
        totalPaidAndDue: number;
        totalDueTodayAndOverdue: number;
        totalDueNextTodayAndOverdue: number;
        totalPastOverdue: number;
        totalProjectedCost: number;
        totalProjectedCommission: number;
        totalDaysInPayPlan: number;
        totalDaysInPolicy: number;
        totalDaysIntoPolicy: number;
        totalDaysIntoPolicyPaidWithoutSpecialPayment: number;
        specialPaymentRunway: number;
        totalDaysPaidFor: number;
        totalPremiumCollateralAmount: number;
        agencyExposureDate: Date;
        datePaidThrough: Date;
        projectedCostPerDay: number;
        daysUntilAgencyExposure: number;
        exposureAmount: number;
        remainingRunway: number;
        isAheadOfPaymentSchedule: boolean;
        commissionByLine: Record<string, ICommissionByLine>;
        totalPayments: number;
        effectiveDate: DateTime;
        expirationDate: DateTime;
        isCompositeArrears: boolean;
        isCompositeAdvanced: boolean;
        isComposite: boolean;
    }
}

export class InstallmentWithDownPaymentPolicyBilling extends PolicyBilling {
    buildPayPlanSchedule(today?: DateTime): void {
        let incrementer = NaN;
        switch (this.policy.paymentPlanId) {
            case PaymentPlan.Id.FOUR_QUARTERLY:
                incrementer = 3;
                break;
            case PaymentPlan.Id.TWO_SEMI_ANNUAL:
                incrementer = 6;
                break;
            default:
                incrementer = 1;
        }

        let nextScheduleStartDate = DateTime.fromDate(this.effectiveDate);
        let monthsToIncrementAfterEffectiveDate = incrementer;
        for (let i = 0; this.length < this.totalPayments; i += incrementer) {
            const nextScheduledEndDate = DateTime.addMonths(this.effectiveDate, monthsToIncrementAfterEffectiveDate).subtractDays(1);
            const premiumDueDate = nextScheduleStartDate;

            this.add({
                index: i,
                startDate: nextScheduleStartDate,
                endDate: nextScheduledEndDate,
                dueDate: premiumDueDate,
                isPast: null,
                isCurrent: null,
                isFuture: null,
            });
            nextScheduleStartDate = DateTime.addDays(nextScheduledEndDate, 1);
            monthsToIncrementAfterEffectiveDate += incrementer;
        }

        this._setPastCurrentFuture(today);
    }

    static getDownPaymentData(billingDetails: PolicyBilling) {
        const firstBillingPeriod = billingDetails.first();
        const invoices = firstBillingPeriod.invoices.filter((inv) =>
            DateTime.isEqual(inv.effectiveDate, billingDetails.effectiveDate, { excludeTime: true })
        );
        const downPaymentAttribute = billingDetails.policy.attributes.find(
            (a) => a.attributeOptionId === AttributeOption.Id.DOWN_PAYMENT_AMOUNT
        );
        const downPaymentCharge = firstBillingPeriod.charges.find(
            (pi) => pi.metadata?.[PaymentIntentMetadata.paymentType] === PaymentIntentMetadata.PaymentType.downPayment
        );
        const invTransactions = invoices
            .map((inv) => {
                return inv.transactions;
            })
            .flat();
        const downPaymentInvoiceTransactions = invTransactions.filter((tran) => {
            const lcDesc = tran.description?.toLowerCase();

            if (["down payment", "dp", "premium"].some((x) => lcDesc?.includes(x))) return tran;
            if (
                tran.premium === downPaymentAttribute?.valueNumber &&
                billingDetails.effectiveDate.isEqual(new Date(tran.policyTransactionEffectiveDate), { excludeTime: true })
            ) {
                return tran;
            }
            return null;
        });

        const totalDownPaymentAmount = downPaymentAttribute
            ? downPaymentAttribute.valueNumber
            : downPaymentInvoiceTransactions.reduce((tot, tran) => {
                  return tot + tran.premium;
              }, 0);
        const totalDownPaymentReceived = downPaymentCharge
            ? downPaymentCharge.amountReceived
            : invoices.reduce((tot, inv) => tot + inv.paid, 0);

        const downPaymentInvoiceIds: string[] = [];
        if (downPaymentInvoiceTransactions.length) {
            downPaymentInvoiceTransactions.map((t) => downPaymentInvoiceIds.push(t.invoiceId));
        }
        if (downPaymentCharge) {
            downPaymentInvoiceIds.push(downPaymentCharge.id);
        }
        if (downPaymentInvoiceIds.length === 0) {
            downPaymentInvoiceIds.push("n/a");
        }

        return {
            attribute: downPaymentAttribute,
            charge: downPaymentCharge,
            invoiceTransactions: downPaymentInvoiceTransactions,
            allInvoiceTransactions: invTransactions,
            totalCharged: totalDownPaymentAmount,
            totalPaid: totalDownPaymentReceived,
            invoiceIds: downPaymentInvoiceIds,
            date: downPaymentInvoiceTransactions
                ? new DateTime(
                      downPaymentInvoiceTransactions.sort((a, b) =>
                          dateSorter(new DateTime(a.policyTransactionEffectiveDate), new DateTime(b.policyTransactionEffectiveDate))
                      )[0]?.policyTransactionEffectiveDate || billingDetails.effectiveDate
                  )
                : downPaymentCharge
                ? downPaymentCharge.createdDate
                : downPaymentAttribute
                ? billingDetails.effectiveDate
                : null,
        };
    }

    static setSpecialPayments(billingDetails: PolicyBilling): void {
        const {
            attribute: downPaymentAttribute,
            charge: downPaymentCharge,
            invoiceTransactions: downPaymentInvoiceTransactions,
            totalCharged: totalDownPaymentAmount,
            totalPaid: totalDownPaymentReceived,
            invoiceIds: downPaymentInvoiceIds,
            date,
        } = InstallmentWithDownPaymentPolicyBilling.getDownPaymentData(billingDetails);

        if (!downPaymentAttribute && !downPaymentCharge && !downPaymentInvoiceTransactions) {
            billingDetails.specialPayment = {
                amount: 0,
                date: null,
                invoiceId: null,
                formattedDate: null,
                isEscrowDeposit: false,
                isDownPayment: false,
                isPaid: null,
            };
            return;
        }

        billingDetails.specialPayment = {
            amount: totalDownPaymentAmount,
            date: date,
            invoiceId: downPaymentInvoiceIds,
            formattedDate: date.format("Mmm dd"),
            isEscrowDeposit: false,
            isDownPayment: true,
            isPaid: totalDownPaymentReceived >= totalDownPaymentAmount,
        };
    }

    setSpecialPayments(): void {
        InstallmentWithDownPaymentPolicyBilling.setSpecialPayments(this);
    }
}

export class InstallmentPolicyBilling extends PolicyBilling {
    buildPayPlanSchedule(today?: DateTime): void {
        let incrementer = NaN;
        switch (this.policy.paymentPlanId) {
            case PaymentPlan.Id.FOUR_QUARTERLY:
                incrementer = 3;
                break;
            case PaymentPlan.Id.TWO_SEMI_ANNUAL:
                incrementer = 6;
                break;
            default:
                incrementer = 1;
        }

        let nextScheduleStartDate = DateTime.fromDate(this.effectiveDate);
        let monthsToIncrementAfterEffectiveDate = incrementer;
        for (let i = 0; this.length < this.totalPayments; i += incrementer) {
            const nextScheduledEndDate = DateTime.addMonths(this.effectiveDate, monthsToIncrementAfterEffectiveDate).subtractDays(1);
            const premiumDueDate = nextScheduleStartDate;

            this.add({
                index: i,
                startDate: nextScheduleStartDate,
                endDate: nextScheduledEndDate,
                dueDate: premiumDueDate,
                isPast: null,
                isCurrent: null,
                isFuture: null,
            });
            nextScheduleStartDate = DateTime.addDays(nextScheduledEndDate, 1);
            monthsToIncrementAfterEffectiveDate += incrementer;
        }

        this._setPastCurrentFuture(today);
    }

    setSpecialPayments(): void {
        this.specialPayment = {
            amount: 0,
            date: null,
            formattedDate: null,
            invoiceId: null,
            isEscrowDeposit: false,
            isDownPayment: false,
            isPaid: null,
        };
    }
}

export class CompositePolicyBilling extends PolicyBilling {
    constructor(policy: Policy, options?: PolicyBilling.IOptions) {
        super(policy, options);
        this.isComposite = true;
    }

    private _buildDoubleAdvancedPayPlanSchedule(): void {
        const firstBillingPeriodStart = DateTime.fromDate(this.effectiveDate);
        const firstBillingPeriodEnd = DateTime.fromDate(firstBillingPeriodStart).addMonths(1).last();
        const firstBillingPeriodDue = DateTime.fromDate(this.effectiveDate);
        firstBillingPeriodDue.setDate(10);

        this.add({
            index: 0,
            startDate: firstBillingPeriodStart,
            endDate: firstBillingPeriodEnd,
            dueDate: firstBillingPeriodDue,
            isPast: null,
            isCurrent: null,
            isFuture: null,
        });

        let nextScheduleStartDate = DateTime.addDays(firstBillingPeriodEnd, 1);
        let nextBillingDueDate = DateTime.addMonths(firstBillingPeriodDue, 1);
        let index = 1;

        do {
            let nextScheduledEndDate = DateTime.last(nextScheduleStartDate);

            if (nextScheduledEndDate.isAfter(this.expirationDate)) {
                nextScheduledEndDate = DateTime.fromDate(this.expirationDate);
            }

            const premiumDueDate = DateTime.fromDate(nextBillingDueDate);

            premiumDueDate.setDate(10);
            this.add({
                index,
                startDate: nextScheduleStartDate,
                endDate: nextScheduledEndDate,
                dueDate: premiumDueDate,
                isPast: null,
                isCurrent: null,
                isFuture: null,
            });
            index++;
            nextScheduleStartDate = DateTime.addDays(nextScheduledEndDate, 1);
            nextBillingDueDate = DateTime.addMonths(nextBillingDueDate, 1);
        } while (nextScheduleStartDate.isBefore(this.expirationDate));

        this._setPastCurrentFuture();
    }

    buildPayPlanSchedule(today?: DateTime): void {
        if (this.isCompositeDoubleAdvanced) {
            return this._buildDoubleAdvancedPayPlanSchedule();
        }

        let nextScheduleStartDate = DateTime.fromDate(this.effectiveDate);
        let index = 0;
        do {
            let nextScheduledEndDate = DateTime.last(nextScheduleStartDate);

            if (nextScheduledEndDate.isAfter(this.expirationDate)) {
                nextScheduledEndDate = DateTime.fromDate(this.expirationDate);
            }

            const premiumDueDate = DateTime.fromDate(nextScheduledEndDate);

            if (this.isCompositeArrears) {
                premiumDueDate.addMonths(1).setDate(10);
            } else if (this.isCompositeAdvanced) {
                premiumDueDate.setDate(10);
            }

            this.add({
                index,
                startDate: nextScheduleStartDate,
                endDate: nextScheduledEndDate,
                dueDate: premiumDueDate,
                isPast: null,
                isCurrent: null,
                isFuture: null,
            });
            index++;
            nextScheduleStartDate = DateTime.addDays(nextScheduledEndDate, 1);
        } while (nextScheduleStartDate.isBefore(this.expirationDate));

        this._setPastCurrentFuture(today);
    }

    setSpecialPayments(): void {
        const firstBillingPeriod = this.first();
        const invTransactions = firstBillingPeriod.invoices
            .map((inv) => {
                return inv.generalLedgerTransactions;
            })
            .flat();
        invTransactions.sort((a, b) => dateSorter(new DateTime(b.enteredDate), new DateTime(a.enteredDate)));
        const escrowDepositAttribute = this.policy.attributes.find((a) => a.attributeOptionId === AttributeOption.Id.ESCROW_DEPOSIT_AMOUNT);

        const escrowDepositInvoiceTransaction = invTransactions.find(
            (glt) => glt.sourceId === 1 && glt.paid > 0 && glt.description?.toLowerCase() === "escrow deposit"
        );
        const escrowDepositCharge = firstBillingPeriod.charges.find(
            (pi) => pi.metadata?.[PaymentIntentMetadata.paymentType] === PaymentIntentMetadata.PaymentType.escrowDeposit
        );
        if (escrowDepositAttribute && !escrowDepositCharge && !escrowDepositInvoiceTransaction) {
            const date = this.effectiveDate;
            this.specialPayment = {
                amount: escrowDepositAttribute.valueNumber,
                date,
                invoiceId: ["n/a"],
                formattedDate: date.format("Mmm dd"),
                isEscrowDeposit: true,
                isDownPayment: false,
                isPaid: false,
            };
            return;
        } else if (escrowDepositAttribute && escrowDepositCharge && !escrowDepositInvoiceTransaction) {
            const date = escrowDepositCharge.metadata[PaymentIntentMetadata.billingPeriodDueDate]
                ? new DateTime(escrowDepositCharge.metadata[PaymentIntentMetadata.billingPeriodDueDate])
                : this.effectiveDate;
            this.specialPayment = {
                amount: escrowDepositAttribute.valueNumber,
                date,
                invoiceId: [escrowDepositCharge.id],
                formattedDate: date.format("Mmm dd"),
                isEscrowDeposit: true,
                isDownPayment: false,
                isPaid: escrowDepositCharge.amountReceived >= escrowDepositAttribute?.valueNumber,
            };
            return;
        } else if (escrowDepositAttribute && escrowDepositCharge && escrowDepositInvoiceTransaction) {
            const invoice = firstBillingPeriod.invoices.find((inv) => inv.id === escrowDepositInvoiceTransaction.invoiceId);
            const date = escrowDepositCharge.metadata[PaymentIntentMetadata.billingPeriodDueDate]
                ? new DateTime(escrowDepositCharge.metadata[PaymentIntentMetadata.billingPeriodDueDate])
                : this.effectiveDate;
            this.specialPayment = {
                amount: escrowDepositAttribute.valueNumber,
                date,
                invoiceId: [escrowDepositCharge.id, invoice.id],
                formattedDate: date.format("Mmm dd"),
                isEscrowDeposit: true,
                isDownPayment: false,
                isPaid: escrowDepositCharge.amountReceived >= escrowDepositAttribute?.valueNumber,
            };
            return;
        } else if (escrowDepositAttribute && !escrowDepositCharge && escrowDepositInvoiceTransaction) {
            const invoice = firstBillingPeriod.invoices.find((inv) => inv.id === escrowDepositInvoiceTransaction.invoiceId);
            const date = invoice.dueDate
                ? new DateTime(invoice.dueDate)
                : invoice.effectiveDate
                ? new DateTime(invoice.effectiveDate)
                : this.effectiveDate;
            this.specialPayment = {
                amount: escrowDepositAttribute.valueNumber,
                date,
                invoiceId: [invoice.id],
                formattedDate: date.format("Mmm dd"),
                isEscrowDeposit: true,
                isDownPayment: false,
                isPaid: invoice.paid >= escrowDepositAttribute?.valueNumber,
            };
            return;
        } else if (!escrowDepositAttribute && !escrowDepositCharge && escrowDepositInvoiceTransaction) {
            const invoice = firstBillingPeriod.invoices.find((inv) => inv.id === escrowDepositInvoiceTransaction.invoiceId);
            const date = invoice.dueDate
                ? new DateTime(invoice.dueDate)
                : invoice.effectiveDate
                ? new DateTime(invoice.effectiveDate)
                : this.effectiveDate;
            this.specialPayment = {
                amount: escrowDepositInvoiceTransaction.paid,
                date,
                invoiceId: [invoice.id],
                formattedDate: date.format("Mmm dd"),
                isEscrowDeposit: true,
                isDownPayment: false,
                isPaid: invoice.paid >= invoice.charged,
            };
            return;
        }

        this.specialPayment = {
            amount: 0,
            date: null,
            invoiceId: null,
            formattedDate: null,
            isEscrowDeposit: false,
            isDownPayment: false,
            isPaid: null,
        };
    }
}

export class PremiumFinancedPolicyBilling extends PolicyBilling {
    buildPayPlanSchedule(): void {
        this.add({
            index: 0,
            startDate: this.effectiveDate,
            endDate: this.expirationDate,
            dueDate: this.effectiveDate,
            isPast: false,
            isCurrent: true,
            isFuture: false,
        });
    }

    setSpecialPayments(): void {
        if (this.isComposite) {
            const {
                attribute: downPaymentAttribute,
                charge: downPaymentCharge,
                invoiceTransactions: downPaymentInvoiceTransactions,
                totalCharged: totalDownPaymentAmount,
                totalPaid: totalDownPaymentReceived,
                invoiceIds: downPaymentInvoiceIds,
                allInvoiceTransactions: invTransactions,
            } = InstallmentWithDownPaymentPolicyBilling.getDownPaymentData(this);

            if (!downPaymentAttribute && !downPaymentCharge && !downPaymentInvoiceTransactions) {
                this.specialPayment = {
                    amount: 0,
                    date: null,
                    invoiceId: null,
                    formattedDate: null,
                    isEscrowDeposit: false,
                    isDownPayment: false,
                    isPaid: null,
                };
                return;
            }

            const amountFinanced = invTransactions.find((it) => {
                if (it.description?.toLowerCase().includes("amount financed") && it.premium > 0) {
                    return it;
                }
                return null;
            });
            const date = new DateTime(amountFinanced.policyTransactionEffectiveDate || this.effectiveDate);
            const amount = totalDownPaymentAmount - amountFinanced.premium;

            this.specialPayment = {
                amount,
                date: date,
                invoiceId: downPaymentInvoiceIds,
                formattedDate: date.format("Mmm dd"),
                isEscrowDeposit: false,
                isDownPayment: true,
                isPaid: totalDownPaymentReceived >= amount,
            };
            return;
        }

        this.specialPayment = {
            amount: 0,
            date: null,
            formattedDate: null,
            invoiceId: null,
            isEscrowDeposit: false,
            isDownPayment: false,
            isPaid: null,
        };
    }
}

export class FullPayPolicyBilling extends PolicyBilling {
    buildPayPlanSchedule(): void {
        this.add({
            index: 0,
            startDate: this.effectiveDate,
            endDate: this.expirationDate,
            dueDate: this.effectiveDate,
            isPast: false,
            isCurrent: true,
            isFuture: false,
        });
    }

    setSpecialPayments(): void {
        this.specialPayment = {
            amount: 0,
            date: null,
            formattedDate: null,
            invoiceId: null,
            isEscrowDeposit: false,
            isDownPayment: false,
            isPaid: null,
        };
    }
}

function getTodayOrExpirationDate(expirationDate: DateTime): DateTime {
    if (DateTime.today.isBefore(expirationDate)) return DateTime.today;
    return expirationDate;
}
